import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { styled } from "@mui/material/styles";
import { ReactComponent as PinIcon } from "assets/PinIconSmall.svg";
import { ReactComponent as UnPinIcon } from "assets/UnPinIconSmall.svg";
import { setSearchText } from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import { isFunction } from "lodash";
import React, { useContext, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch } from "redux/hooks";
import { setSortingForSpecificCase } from "redux/sorting.slice";
import { tokens } from "styles/Tokens";
import { resetPageModifiedFilter } from "./Filters/GlobalFilters.slice";
import "./Tabs.scss";
import ToolTip from "./Tooltip";
import Typography from "./Typography";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  sx?: {};
}

const TabPanel = styled((props: TabPanelProps) => {
  const { children, value, index, sx, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
})<TabPanelProps>({});

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`
  };
}

export default function Basictabs(props: any) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobileScreen } = useContext(ThemeContext);
  const dispatch = useAppDispatch();
  const newSearchParams = new URLSearchParams(searchParams);
  const getDefaultTab = () => {
    if (searchParams.get("parentTab")) {
      let index1 = tabDetails.findIndex((obj: any) => obj.label === searchParams.get("parentTab"));
      if (index1 >= 0) return index1;
      else if (searchParams.get("childTab"))
        return tabDetails.findIndex((obj: any) => obj.label === searchParams.get("childTab"));
      else return props.defaultTab || 0;
    } else return props.defaultTab || 0;
  };

  const {
    tabDetails,
    alignItems = "center",
    selectedTab,
    defaultTab = getDefaultTab(),
    labelVariant = "body1",
    whiteVariant = false,
    gap = "40px",
    backgroundColor,
    marginBottom,
    sx,
    isParentTab = false,
    isChildTab = false,
    getCurrentTab,
    divId = "",
    onTabChangeFn = Function,
    handlePinUnpin,
    pinIcon = false,
    PinIconProp,
    showIconOnHover,
    UnPinIconProp,
    preventParentTab = false,
    className = "",
    shouldAppendTabParam = false
  } = props;

  const [value, setValue] = React.useState(defaultTab);

  useEffect(() => {
    if (!preventParentTab && searchParams.get("parentTab")) {
      let index1 = tabDetails.findIndex((obj: any) => obj.label === searchParams.get("parentTab"));
      if (index1 >= 0) {
        setValue(index1);
        return;
      } else if (searchParams.get("childTab")) {
        setValue(tabDetails.findIndex((obj: any) => obj.label === searchParams.get("childTab")));
        return;
      }
    } else setValue(defaultTab);
  }, [selectedTab, defaultTab]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    const filtersToClear = tabDetails[value].filtersToClear || [];
    if (!props.retainFilters && filtersToClear.length) {
      filtersToClear.map((pageName: any) => dispatch(resetPageModifiedFilter({ pageName })));
    }
    if (tabDetails[value]?.searchTextClear) dispatch(setSearchText(""));
    if (onTabChangeFn) onTabChangeFn(newValue);
    setValue(newValue);
    if (isFunction(selectedTab)) selectedTab(newValue);
    if (newSearchParams.get("parentTab") === "Property Plans") dispatch(setSortingForSpecificCase({ reset: true }));
    // searchParams.delete("search");
    // navigate(`${location.pathname}?${searchParams.toString()}`, {
    //   replace: true
    // });
  };

  useEffect(() => {
    let updateFlag = false;
    if (getCurrentTab) {
      getCurrentTab(value);
    }
    if (isParentTab && newSearchParams.get("parentTab") !== tabDetails[value]?.label) {
      updateFlag = true;
      if (newSearchParams.get("childTab") && value === 0) {
        newSearchParams.set("parentTab", tabDetails[value]?.label);
        newSearchParams.set("childTab", newSearchParams.get("childTab") ?? "");
      } else {
        newSearchParams.set("parentTab", tabDetails[value]?.label);
        newSearchParams.delete("childTab");
      }
    }

    if (isChildTab) {
      updateFlag = true;
      newSearchParams.set("parentTab", newSearchParams.get("parentTab") ?? "");
      newSearchParams.set("childTab", tabDetails[value]?.label);
    }
    if (shouldAppendTabParam) {
      updateFlag = true;
      newSearchParams.set("tab", tabDetails[value]?.label);
    }
    if (updateFlag)
      navigate(`${location.pathname}?${newSearchParams.toString()}`, {
        state: location?.state,
        replace: true
      });
  }, [value]);

  const handlePinChange = (e: any, value: string) => {
    e.stopPropagation();
    handlePinUnpin(value);
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: backgroundColor ?? tokens.Background.General.Secondary,
        alignItems: "center",
        borderRadius: isChildTab ? "12px" : 0
      }}
    >
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          width: "100%",
          display: "flex",
          justifyContent: alignItems,
          paddingX: alignItems ? 0 : "20px",
          marginBottom: marginBottom || (isMobileScreen ? 3 : "32px")
        }}
        className={`tab-container ${className}`}
      >
        <Tabs
          textColor={whiteVariant ? "secondary" : "primary"}
          TabIndicatorProps={whiteVariant ? { style: { background: "white" } } : {}}
          sx={{
            ".MuiTabs-flexContainer": { gap: isMobileScreen ? "16px" : gap, ...sx },
            "&.Mui-selected": {
              opacity: 1,
              "&:hover": {
                opacity: 1
              }
            }
          }}
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
          variant="scrollable"
          scrollButtons="auto"
          className="individual-tab"
        >
          {tabDetails?.map((element: any, index: number) => {
            return (
              <Tab
                className={
                  index !== props.pinTabIndex && showIconOnHover && value === props.pinTabIndex ? "unPinned-icon" : ""
                }
                disableRipple
                sx={{
                  width: "fit-content",
                  minWidth: "auto",
                  paddingX: "0",
                  "&.Mui-selected": {
                    opacity: 1,
                    "&:hover": {
                      opacity: 1
                    }
                  }
                }}
                key={element.label}
                disabled={element.isDisabled}
                label={
                  <Box display={"flex"} flexDirection={"row"} gap={"8px"}>
                    <Typography showBold variant={labelVariant}>
                      {element.label}
                    </Typography>
                    {pinIcon ? (
                      props.pinTabIndex === index ? (
                        PinIconProp ? (
                          <PinIconProp onClick={(e: any) => handlePinChange(e, props.unPinTab)} />
                        ) : (
                          <PinIcon onClick={(e: any) => handlePinChange(e, props.unPinTab)} />
                        )
                      ) : (
                        <ToolTip title="Pin this tab to make this the default tab">
                          {UnPinIconProp ? (
                            <UnPinIconProp onClick={(e: any) => handlePinChange(e, element.pinValue)} />
                          ) : (
                            <UnPinIcon onClick={(e: any) => handlePinChange(e, element.pinValue)} />
                          )}
                        </ToolTip>
                      )
                    ) : null}

                    {element.badge ? element.badge : <></>}
                  </Box>
                }
                {...a11yProps(index)}
              />
            );
          })}
        </Tabs>
      </Box>
      {divId ? (
        <Box id={divId} sx={{ ...sx }}>
          {tabDetails?.map((element: any, index: number) => {
            return (
              <TabPanel key={element.label} value={value} index={index} sx={{ width: "100%" }}>
                <element.content></element.content>
              </TabPanel>
            );
          })}
        </Box>
      ) : (
        <>
          {tabDetails?.map((element: any, index: number) => {
            return (
              <TabPanel key={element.label} value={value} index={index} sx={{ width: "100%" }}>
                <element.content></element.content>
              </TabPanel>
            );
          })}
        </>
      )}
    </Box>
  );
}
